'use client';

import { useState, useEffect } from 'react';

interface ImprintClientProps {
  lang: 'en' | 'de';
}

export default function ImprintClient({ lang: initialLang }: ImprintClientProps) {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(initialLang);

  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: 'en' | 'de' }>) => {
      setCurrentLanguage(event.detail.language);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const allContent = {
    en: {
      title: 'Imprint',
      subtitle: 'Legal Information pursuant to § 5 DDG (Digitale-Dienste-Gesetz)',
      effective: 'Effective: March 2026',
      sections: [
        {
          heading: 'Business Owner',
          content: 'Stefan Artmann',
        },
        {
          heading: 'Business Name',
          content: 'Planet Pulse Studios\n(Sole proprietorship / Einzelunternehmen — not a registered company)',
        },
        {
          heading: 'Address',
          content: 'Lütjenmoor 32\n22850 Norderstedt\nGermany',
        },
        {
          heading: 'Contact',
          type: 'contact',
          phone: '+49 151 583 578 26',
          email: 'stefanartmann@planetpulsestudios.com',
        },
        {
          heading: 'VAT Identification Number',
          content: 'DE361273463\n(pursuant to § 27a Umsatzsteuergesetz)',
        },
        {
          heading: 'Responsible for Content',
          content: 'Stefan Artmann\n(pursuant to § 18 Abs. 2 MStV — Medienstaatsvertrag)\nAddress as above.',
        },
        {
          heading: 'Supervisory Authority',
          content: 'As a sole trader operating an information society service, Planet Pulse Studios is not subject to a licensing supervisory authority. The competent data protection supervisory authority is:\n\nUnabhängiges Landeszentrum für Datenschutz Schleswig-Holstein (ULD)\nHolstenstraße 98, 24103 Kiel, Germany\nPhone: +49 431 988 1200\nEmail: mail@datenschutzzentrum.de',
        },
        {
          heading: 'Professional Liability',
          content: 'Stefan Artmann operates as a sole trader (Einzelunternehmer) under German commercial law. No mandatory professional liability insurance is prescribed for this business category. Voluntary professional indemnity insurance is maintained separately.',
        },
        {
          heading: 'Dispute Resolution',
          content: 'The European Commission provides an online dispute resolution (ODR) platform: https://ec.europa.eu/consumers/odr\n\nPlanet Pulse Studios does not participate in dispute resolution proceedings before a consumer arbitration board (Verbraucherschlichtungsstelle) and is not obliged to do so. Services are provided exclusively to business clients (B2B).',
        },
        {
          heading: 'Disclaimer',
          content: 'Despite careful content review, we accept no liability for the content of external links. The operators of linked pages are solely responsible for their content.',
        },
      ],
    },
    de: {
      title: 'Impressum',
      subtitle: 'Rechtliche Informationen gemäß § 5 DDG (Digitale-Dienste-Gesetz)',
      effective: 'Stand: März 2026',
      sections: [
        {
          heading: 'Geschäftsinhaber',
          content: 'Stefan Artmann',
        },
        {
          heading: 'Unternehmensname',
          content: 'Planet Pulse Studios\n(Einzelunternehmen — kein eingetragenes Unternehmen)',
        },
        {
          heading: 'Anschrift',
          content: 'Lütjenmoor 32\n22850 Norderstedt\nDeutschland',
        },
        {
          heading: 'Kontakt',
          type: 'contact',
          phone: '+49 151 583 578 26',
          email: 'stefanartmann@planetpulsestudios.com',
        },
        {
          heading: 'Umsatzsteuer-Identifikationsnummer',
          content: 'DE361273463\n(gemäß § 27a Umsatzsteuergesetz)',
        },
        {
          heading: 'Verantwortlich für den Inhalt',
          content: 'Stefan Artmann\n(gemäß § 18 Abs. 2 MStV — Medienstaatsvertrag)\nAnschrift wie oben.',
        },
        {
          heading: 'Aufsichtsbehörde',
          content: 'Als Einzelunternehmer, der einen Dienst der Informationsgesellschaft betreibt, unterliegt Planet Pulse Studios keiner Zulassungsaufsichtsbehörde. Die zuständige Datenschutzaufsichtsbehörde ist:\n\nUnabhängiges Landeszentrum für Datenschutz Schleswig-Holstein (ULD)\nHolstenstraße 98, 24103 Kiel\nTelefon: +49 431 988 1200\nE-Mail: mail@datenschutzzentrum.de',
        },
        {
          heading: 'Berufshaftung',
          content: 'Stefan Artmann ist als Einzelunternehmer nach deutschem Handelsrecht tätig. Für diese Unternehmenskategorie ist keine Pflichtberufshaftpflichtversicherung vorgeschrieben. Eine freiwillige Berufshaftpflichtversicherung wird separat unterhalten.',
        },
        {
          heading: 'Streitbeilegung',
          content: 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr\n\nPlanet Pulse Studios nimmt nicht an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teil und ist hierzu auch nicht verpflichtet. Leistungen werden ausschließlich an Geschäftskunden (B2B) erbracht.',
        },
        {
          heading: 'Haftungsausschluss',
          content: 'Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.',
        },
      ],
    },
  };

  const t = allContent[currentLanguage];

  const renderContent = (text: string) => {
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  return (
    <main className="relative z-10 pt-40 pb-20">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-secondary/50 backdrop-blur-md rounded-xl p-8 md:p-12 border border-border shadow-cosmic">
          <h1 className="font-exo text-heading-xl md:text-hero-mobile text-accent mb-2">
            {t.title}
          </h1>
          <p className="text-foreground-secondary text-body-lg mb-1">
            {t.subtitle}
          </p>
          <p className="text-foreground-secondary text-body-sm mb-12">
            {t.effective}
          </p>

          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <section key={index}>
                <h2 className="font-exo text-heading-md text-highlight mb-3">
                  {section.heading}
                </h2>
                {'type' in section && section.type === 'contact' ? (
                  <p className="text-foreground-light text-body-md">
                    <span className="block mb-2">
                      <span className="text-foreground-secondary">{currentLanguage === 'de' ? 'Telefon: ' : 'Phone: '}</span>
                      <a href={`tel:${section.phone}`} className="hover:text-accent transition-colors">
                        {section.phone}
                      </a>
                    </span>
                    <span className="block">
                      <span className="text-foreground-secondary">E-Mail: </span>
                      <a href={`mailto:${section.email}`} className="hover:text-accent transition-colors">
                        {section.email}
                      </a>
                    </span>
                  </p>
                ) : (
                  <p className="text-foreground-light text-body-md leading-relaxed">
                    {'content' in section && renderContent(section.content)}
                  </p>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}